import { ref } from 'vue';
import { defineStore } from 'pinia';

/**
 * Links configuration from customer-specific sources (env or API)
 */
export interface CustomerLinks {
  serviceAgreement?: string;
  privacyPolicy?: string;
}

export type CustomerPromoMediaType = 'video' | 'image';

export interface CustomerPromoMedia {
  src: string;
  type: CustomerPromoMediaType;
}

export interface CustomerMedia {
  totalBalancePromoMedia?: CustomerPromoMedia;
}

export interface CustomerCountries {
  registration?: readonly string[];
  phone?: readonly string[];
}

export type CustomerNovuContextValue =
  | string
  | {
      id: string;
      data?: Record<string, unknown>;
    };

export type CustomerNovuContext = Partial<Record<string, CustomerNovuContextValue>>;

export interface CustomerNotifications {
  novuContext?: CustomerNovuContext;
  novuContextHash?: string;
}

export interface CustomerDateTime {
  locale?: Intl.LocalesArgument;
  pattern?: string;
}

export interface CustomerCurrencyFormat {
  transactionAmount?: {
    pattern?: string;
    locale?: Intl.LocalesArgument;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  };
}

/**
 * Customer configuration interface for initialization
 */
export interface CustomerConfig {
  links?: CustomerLinks;
  media?: CustomerMedia;
  countries?: CustomerCountries;
  notifications?: CustomerNotifications;
  dateTime?: CustomerDateTime;
  currency?: CustomerCurrencyFormat;
}

/**
 * Customer configuration state that can be sourced from:
 * - Build-time environment variables (initial/default)
 * - API (future enhancement)
 *
 * This store provides a single reactive source of truth for customer-specific
 * configuration like legal links, branding assets, etc.
 *
 * @example
 * // Initialize with env values in main.ts
 * const configStore = useCustomerConfigStore();
 * configStore.initialize({
 *   links: {
 *     serviceAgreement: import.meta.env.VITE_SERVICE_AGREEMENT_URL,
 *     privacyPolicy: import.meta.env.VITE_PRIVACY_POLICY_URL,
 *   }
 * });
 *
 * @example
 * // Later, fetch from API and merge
 * await configStore.fetchFromApi();
 *
 * @example
 * // Use in components
 * const { links } = storeToRefs(useCustomerConfigStore());
 * // or
 * const configStore = useCustomerConfigStore();
 * // links are reactive: configStore.links.serviceAgreement
 */
export const useCustomerConfigStore = defineStore('customer-config', () => {
  // State
  const links = ref<CustomerLinks>({});
  const media = ref<CustomerMedia>({});
  const countries = ref<CustomerCountries>({});
  const notifications = ref<CustomerNotifications>({});
  const dateTime = ref<CustomerDateTime>({});
  const currency = ref<CustomerCurrencyFormat>({});

  // Initialize with default/env values
  const initialize = (config: CustomerConfig) => {
    if (config.links) {
      links.value = { ...config.links };
    }

    if (config.media) {
      media.value = { ...config.media };
    }

    if (config.countries) {
      countries.value = { ...config.countries };
    }

    if (config.notifications) {
      notifications.value = { ...config.notifications };
    }

    if (config.dateTime) {
      dateTime.value = { ...config.dateTime };
    }

    if (config.currency) {
      currency.value = { ...config.currency };
    }
  };

  // Future: fetch from API and merge/update
  // This is a placeholder for when the API is ready
  const fetchFromApi = async () => {
    // TODO: Implement when API endpoint is available
    // Example implementation:
    // const response = await fetch('/api/v1/customer/config');
    // const apiConfig = await response.json();
    // links.value = { ...links.value, ...apiConfig.links };
  };

  // Update specific values (useful for hot-reloading or partial updates)
  const updateLinks = (newLinks: Partial<CustomerLinks>) => {
    links.value = { ...links.value, ...newLinks };
  };

  const updateMedia = (newMedia: Partial<CustomerMedia>) => {
    media.value = { ...media.value, ...newMedia };
  };

  const updateCountries = (newCountries: Partial<CustomerCountries>) => {
    countries.value = { ...countries.value, ...newCountries };
  };

  const updateNotifications = (newNotifications: Partial<CustomerNotifications>) => {
    notifications.value = { ...notifications.value, ...newNotifications };
  };

  const updateDateTime = (newDateTime: Partial<CustomerDateTime>) => {
    dateTime.value = { ...dateTime.value, ...newDateTime };
  };

  const updateCurrency = (newCurrency: Partial<CustomerCurrencyFormat>) => {
    currency.value = { ...currency.value, ...newCurrency };
  };

  return {
    links,
    media,
    countries,
    notifications,
    dateTime,
    currency,
    initialize,
    fetchFromApi,
    updateLinks,
    updateMedia,
    updateCountries,
    updateNotifications,
    updateDateTime,
    updateCurrency,
  };
});
